// GL CONTEXT TUNING : fluid-bg's engine always requests its WebGL context
// with `preserveDrawingBuffer: true` and no `antialias`/`powerPreference`
// hints (see node_modules/fluid-bg/dist/core.js `_initGL()`), and exposes no
// option to change this. `preserveDrawingBuffer` forces the browser to
// retain/copy the full framebuffer after every frame instead of just
// swapping buffers — cheap for Chrome's compositor to hide, expensive on
// Firefox's and on mobile GPUs, which is exactly where the lag shows up.
// (It's safe to drop: the engine's own `toDataURL()` re-renders synchronously
// right before reading the canvas, so it never relied on the retained buffer.)
//
// Same targeted-patch approach as fluidBgEye.js's shader patch: briefly
// intercept HTMLCanvasElement.getContext for the duration of the mount call
// and override just the attributes the engine passes in.
export function withTunedGLContext(fn) {
  const proto = HTMLCanvasElement.prototype;
  const original = proto.getContext;
  proto.getContext = function (type, attrs) {
    if (type === 'webgl' || type === 'experimental-webgl') {
      attrs = {
        ...attrs,
        preserveDrawingBuffer: false,
        antialias: false,
        powerPreference: 'high-performance',
      };
    }
    return original.call(this, type, attrs);
  };
  try {
    return fn();
  } finally {
    proto.getContext = original;
  }
}

// RENDER SCALE : fluid-bg has no knob for render resolution — `_resize()`
// always sizes the drawing buffer to the full container box at up to 2x
// devicePixelRatio. The fragment shader it runs there blends 3 fields, a
// dither screen, and the melted eye texture — real per-pixel cost that
// scales with the pixel count. Firefox's GL backend (and mobile GPUs,
// which also have far less headroom than a desktop/laptop Chrome tab) feel
// that cost much more than Chrome does.
// There's no public option to cap it, so we shrink the actual DOM box fed
// to the engine (`clientWidth`/`clientHeight`, which `_resize()` reads) and
// scale it back up visually with CSS — the GPU then does a cheap texture
// blit to cover the full area instead of re-running the shader at full
// resolution. See the `.fluid-bg-host` transform in EntryEye.js.
export function getFluidRenderScale() {
  if (typeof window === 'undefined') return 1;
  const isMobile = window.matchMedia('(max-width: 768px)').matches;
  const isFirefox = /firefox/i.test(navigator.userAgent);
  if (isMobile) return 0.5;
  if (isFirefox) return 0.5;
  return 1;
}
